import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { environment } from 'src/environments/environment';
import { UtilsService } from 'src/app/services/utils.service';

@Component({
  selector: 'app-configure-bisual-mcp-dialog',
  templateUrl: './configure-bisual-mcp-dialog.component.html',
  styleUrl: './configure-bisual-mcp-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    CdkScrollable,
    MatButton,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatExpansionModule,
  ],
})
export class ConfigureBisualMcpDialogComponent {
  private readonly dialog_ref = inject(MatDialogRef<ConfigureBisualMcpDialogComponent>);
  private readonly utils = inject(UtilsService);
  private readonly snack = inject(MatSnackBar);

  readonly mcp_server_name = 'bisual';
  readonly mcp_url = `${environment.laravel_url}/mcp/bisual`;

  readonly cursor_install_url = buildCursorInstallUrl(this.mcp_server_name, this.mcp_url);
  readonly vscode_install_url = buildVscodeInstallUrl(this.mcp_server_name, this.mcp_url);
  readonly claude_cli_command = `claude mcp add --transport http ${this.mcp_server_name} ${this.mcp_url} --scope user`;

  readonly cursor_manual_json = JSON.stringify(
    {
      mcpServers: {
        [this.mcp_server_name]: {
          url: this.mcp_url,
        },
      },
    },
    null,
    2
  );

  readonly vscode_manual_json = JSON.stringify(
    {
      servers: {
        [this.mcp_server_name]: {
          type: 'http',
          url: this.mcp_url,
        },
      },
    },
    null,
    2
  );

  readonly claude_manual_json = JSON.stringify(
    {
      mcpServers: {
        [this.mcp_server_name]: {
          type: 'http',
          url: this.mcp_url,
        },
      },
    },
    null,
    2
  );

  close(): void {
    this.dialog_ref.close();
  }

  openCursorInstall(): void {
    window.open(this.cursor_install_url, '_blank');
  }

  openVscodeInstall(): void {
    window.open(this.vscode_install_url, '_blank');
  }

  async copyMcpUrl(): Promise<void> {
    await this.copyText(this.mcp_url, 'URL del MCP copiada');
  }

  async copyClaudeCommand(): Promise<void> {
    await this.copyText(this.claude_cli_command, 'Comando de Claude Code copiado');
  }

  async copyCursorJson(): Promise<void> {
    await this.copyText(this.cursor_manual_json, 'JSON de Cursor copiado');
  }

  async copyVscodeJson(): Promise<void> {
    await this.copyText(this.vscode_manual_json, 'JSON de VS Code copiado');
  }

  async copyClaudeJson(): Promise<void> {
    await this.copyText(this.claude_manual_json, 'JSON de Claude Code copiado');
  }

  private async copyText(text: string, success_message: string): Promise<void> {
    await this.utils.copyTextToClipboard(text);
    this.snack.open(success_message, 'Ok', { duration: 2500 });
  }
}

function buildCursorInstallUrl(name: string, mcp_url: string): string {
  const config = { type: 'http', url: mcp_url };
  const config_base64 = encodeUtf8ToBase64Url(JSON.stringify(config));

  return `cursor://anysphere.cursor-deeplink/mcp/install?name=${encodeURIComponent(name)}&config=${config_base64}`;
}

function buildVscodeInstallUrl(name: string, mcp_url: string): string {
  const config = {
    name,
    type: 'http',
    url: mcp_url,
  };

  return `vscode:mcp/install?${encodeURIComponent(JSON.stringify(config))}`;
}

function encodeUtf8ToBase64Url(text: string): string {
  const bytes = new TextEncoder().encode(text);
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
